import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { adminApi } from "@/lib/auth-api";
import type { DynamicRole } from "@/lib/auth-types";

interface UseRolesOptions {
	/**
	 * Set to false on pages mounted for every user (e.g. <Visible>) — backend
	 * `/admin/roles` requires admin and 403s for regular users (Sentry
	 * FARMASTER-FRONTEND-1Y). Defaults to true for admin pages.
	 */
	enabled?: boolean;
}

export function useRoles(options: UseRolesOptions = {}) {
	const { enabled = true } = options;
	const {
		data: roles = [],
		isError,
		error,
		isLoading,
		...rest
	} = useQuery<DynamicRole[]>({
		queryKey: ["roles"],
		queryFn: adminApi.getRoles,
		staleTime: 5 * 60 * 1000, // 5 min cache
		enabled,
	});

	const getRoleLabel = (role: string): string => {
		const found = roles.find((r) => r.role === role);
		return found?.name ?? role;
	};

	const roleHierarchy = useMemo(
		() => [...roles].sort((a, b) => a.sortOrder - b.sortOrder).map((r) => r.role),
		[roles],
	);

	const getRoleLevel = (role: string): number => {
		return roleHierarchy.indexOf(role);
	};

	/** Admin role keys derived from backend is_admin flag */
	const adminRoles = useMemo(() => roles.filter((r) => r.isAdmin).map((r) => r.role), [roles]);

	return {
		roles,
		adminRoles,
		getRoleLabel,
		roleHierarchy,
		getRoleLevel,
		isError,
		error,
		isLoading,
		...rest,
	};
}
